import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { api, imageUrl, API_BASE, formatErr } from "@/lib/api";
import UploadZone from "@/components/UploadZone";
import ProcessingAnimation from "@/components/ProcessingAnimation";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import { contrastText, BRAND_DEFAULT } from "@/lib/color";
import { ArrowRight, Sparkles, Phone, MapPin } from "lucide-react";

const STEPS = { intro: 0, form: 1, processing: 2, result: 3, notfound: -1, unavailable: 97, demo: 99 };

export default function PatientPreview() {
  const { slug } = useParams();
  const [clinic, setClinic] = useState(null);
  const [step, setStep] = useState(STEPS.intro);
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState("");
  const [form, setForm] = useState({ patient_name: "", patient_email: "", patient_phone: "" });
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api
      .get(`/clinics/${slug}/public`)
      .then((res) => {
        setClinic(res.data);
        if (res.data?.is_marketing_demo) setStep(STEPS.demo);
        else if (res.data?.read_only) setStep(STEPS.unavailable);
      })
      .catch(() => setStep(STEPS.notfound));
  }, [slug]);

  useEffect(() => {
    if (!file) {
      setFilePreview("");
      return;
    }
    const url = URL.createObjectURL(file);
    setFilePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      setErr("Please upload a smiling photo");
      return;
    }
    if (!form.patient_name || !form.patient_email) {
      setErr("Please share your name and email so the clinic can send your result.");
      return;
    }
    setErr("");
    setStep(STEPS.processing);
    try {
      const fd = new FormData();
      fd.append("patient_name", form.patient_name);
      fd.append("patient_email", form.patient_email);
      fd.append("patient_phone", form.patient_phone || "");
      fd.append("image", file);
      // Use raw axios so we don't add Bearer token from interceptor (public route)
      const { data } = await axios.post(`${API_BASE}/clinics/${slug}/preview`, fd, { timeout: 180000 });
      setResult(data);
      setStep(STEPS.result);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Something went wrong while generating your smile.");
      setStep(STEPS.form);
    }
  };

  if (step === STEPS.notfound) {
    return (
      <div className="min-h-screen bg-bone-100 flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="overline">404</div>
          <h1 className="font-heading text-4xl text-forest-700 mt-4">Clinic not found</h1>
          <p className="text-sm text-forest-400 mt-3">
            This smile preview link doesn't match any clinic. Please double-check the URL.
          </p>
          <Link to="/" className="btn-secondary mt-8 inline-flex" data-testid="back-home-link">Back to home</Link>
        </div>
      </div>
    );
  }

  if (!clinic) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bone-100">
        <div className="overline">Loading…</div>
      </div>
    );
  }

  const brand = clinic.primary_color || BRAND_DEFAULT;
  const brandStyle = { backgroundColor: brand, color: contrastText(brand), borderColor: brand };

  if (step === STEPS.demo) {
    return <MarketingDemo />;
  }

  if (step === STEPS.unavailable) {
    return (
      <div className="min-h-screen bg-bone-100">
        <ClinicHeader clinic={clinic} />
        <main className="max-w-xl mx-auto px-6 py-16 md:py-24 text-center" data-testid="patient-unavailable">
          <Sparkles size={30} strokeWidth={1.3} className="text-forest-600 mx-auto" />
          <h1 className="font-heading text-4xl md:text-5xl text-forest-700 mt-6 leading-[1.05]">
            Smile previews are temporarily unavailable.
          </h1>
          <p className="text-forest-400 mt-5 leading-relaxed">
            {clinic.clinic_name} isn't taking new AI smile previews right now. Please call us to
            book a consultation. We'd be glad to talk through your smile in person.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            {clinic.booking_url && (
              <a href={clinic.booking_url} target="_blank" rel="noreferrer" className="btn-primary" style={brandStyle} data-testid="unavailable-book-btn">
                Book a consultation <ArrowRight size={16} />
              </a>
            )}
            {clinic.phone && (
              <a href={`tel:${clinic.phone}`} className="btn-secondary" data-testid="unavailable-call-btn">
                <Phone size={14} /> {clinic.phone}
              </a>
            )}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone-100">
      <ClinicHeader clinic={clinic} />

      <main className="max-w-xl mx-auto px-6 py-12 md:py-20">
        <AnimatePresence mode="wait">
          {step === STEPS.intro && (
            <motion.div key="intro" {...fade}>
              <div className="overline">A gift from {clinic.clinic_name}</div>
              <h1 className="font-heading text-5xl md:text-6xl text-forest-700 mt-4 leading-[1.05]">
                See your future smile.
              </h1>
              <p className="mt-6 text-forest-400 leading-relaxed">
                Upload a recent selfie smiling. In about 30 seconds, our
                clinical AI will show you what your teeth could look like after
                {" "}{clinic.treatment_name || "clear aligner"} treatment with {clinic.clinic_name}. It's
                non-binding, and your photo stays private.
              </p>
              <button onClick={() => setStep(STEPS.form)} data-testid="intro-start" className="btn-primary mt-10" style={brandStyle}>
                Start my preview <ArrowRight size={16} />
              </button>
              <div className="mt-12 grid grid-cols-3 gap-4 text-xs">
                <Tile n="30s" t="render time" />
                <Tile n="Free" t="no payment" />
                <Tile n="Private" t="your photo stays yours" />
              </div>
            </motion.div>
          )}

          {step === STEPS.form && (
            <motion.form key="form" {...fade} onSubmit={submit} data-testid="patient-form">
              <div className="overline">Step 1 of 2</div>
              <h1 className="font-heading text-4xl text-forest-700 mt-3">Your smile photo</h1>
              <div className="mt-8">
                <UploadZone file={file} onFileSelected={setFile} />
              </div>

              <div className="mt-10">
                <div className="overline mb-2">Step 2 of 2</div>
                <h2 className="font-heading text-3xl text-forest-700">Where should we send it?</h2>
                <div className="mt-6 space-y-5">
                  <Field
                    label="Full name"
                    value={form.patient_name}
                    onChange={(v) => setForm({ ...form, patient_name: v })}
                    testid="patient-name"
                    required
                  />
                  <Field
                    label="Email"
                    type="email"
                    value={form.patient_email}
                    onChange={(v) => setForm({ ...form, patient_email: v })}
                    testid="patient-email"
                    required
                  />
                  <Field
                    label="Phone (optional)"
                    value={form.patient_phone}
                    onChange={(v) => setForm({ ...form, patient_phone: v })}
                    testid="patient-phone"
                  />
                </div>
              </div>

              {err && (
                <div className="text-sm text-clay-600 border border-clay-600/30 bg-clay-400/10 px-3 py-2 mt-6" data-testid="patient-error">
                  {err}
                </div>
              )}

              <button type="submit" className="btn-primary mt-8" style={brandStyle} data-testid="patient-submit">
                Generate my smile preview <ArrowRight size={16} />
              </button>
              <p className="text-xs text-forest-400 mt-4 leading-relaxed">
                By submitting, you allow {clinic.clinic_name} to contact you about
                this preview. We never share your photo with third parties.
              </p>
            </motion.form>
          )}

          {step === STEPS.processing && (
            <motion.div key="processing" {...fade}>
              <ProcessingAnimation imageSrc={filePreview} />
            </motion.div>
          )}

          {step === STEPS.result && result && (
            <motion.div key="result" {...fade} className="space-y-8" data-testid="patient-result">
              <div>
                <div className="overline">Your AI smile preview</div>
                <h1 className="font-heading text-4xl text-forest-700 mt-3">
                  Hello, {form.patient_name.split(" ")[0]}. Meet your future smile.
                </h1>
                <p className="text-forest-400 mt-3 text-sm">
                  Drag the handle to compare. This is a non-binding visualization.
                </p>
              </div>
              <BeforeAfterSlider
                beforeSrc={imageUrl(result.before_url)}
                afterSrc={imageUrl(result.after_url)}
                aspect="aspect-[4/5]"
                brandColor={brand}
              />
              <div className="bg-white border border-bone-200 p-6 md:p-8">
                <div className="overline">Next step</div>
                <h3 className="font-heading text-2xl text-forest-700 mt-2">
                  Book a free consultation with {clinic.clinic_name}.
                </h3>
                <p className="text-sm text-forest-400 mt-2">
                  A 30-minute consult to see if this is the right fit.
                </p>
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  {clinic.booking_url ? (
                    <a href={clinic.booking_url} target="_blank" rel="noreferrer" className="btn-primary" style={brandStyle} data-testid="book-consult-btn">
                      Book a free consult <ArrowRight size={16} />
                    </a>
                  ) : (
                    <button className="btn-primary" style={brandStyle} data-testid="book-consult-btn" onClick={() => alert("Clinic has been notified. They'll reach out shortly.")}>
                      Book a free consult <ArrowRight size={16} />
                    </button>
                  )}
                  {clinic.phone && (
                    <a href={`tel:${clinic.phone}`} className="btn-secondary">
                      <Phone size={14} /> {clinic.phone}
                    </a>
                  )}
                </div>
              </div>
              <p className="text-xs text-forest-400 text-center pt-4">
                Want this for your clinic?{" "}
                <Link to="/" className="underline underline-offset-4">Built on Toothpix</Link>
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}

const fade = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.45, ease: "easeOut" },
};

function ClinicHeader({ clinic }) {
  return (
    <header className="border-b border-bone-200 bg-white">
      <div className="max-w-xl mx-auto px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {clinic.logo_url ? (
            <img src={clinic.logo_url} alt="" className="w-8 h-8 object-contain" />
          ) : (
            <Sparkles size={20} strokeWidth={1.5} style={{ color: clinic.primary_color || "#1C352D" }} />
          )}
          <div>
            <div className="font-heading text-lg leading-none text-forest-700">{clinic.clinic_name}</div>
            <div className="overline mt-1">{clinic.clinic_tagline || "Clear Aligner Provider"}</div>
          </div>
        </div>
        {clinic.address && (
          <div className="hidden sm:flex items-center gap-1 text-xs text-forest-400">
            <MapPin size={12} /> {clinic.address}
          </div>
        )}
      </div>
    </header>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="label-flat">{label}</label>
      <input
        type={type}
        className="input-flat"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
      />
    </div>
  );
}

function Tile({ n, t }) {
  return (
    <div className="bg-white border border-bone-200 p-3 text-center">
      <div className="font-heading text-xl text-forest-700">{n}</div>
      <div className="overline mt-1 !text-[10px]">{t}</div>
    </div>
  );
}

function MarketingDemo() {
  const [demo, setDemo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchDemo = async (attempt = 0) => {
      try {
        const { data } = await api.get("/demo/preview");
        if (!cancelled) setDemo(data);
      } catch (e) {
        if (!cancelled && attempt < 10) setTimeout(() => fetchDemo(attempt + 1), 5000);
      }
    };
    fetchDemo();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-screen bg-bone-100">
      <header className="border-b border-bone-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="demo-brand">
            <Sparkles size={20} strokeWidth={1.5} className="text-forest-600" />
            <span className="font-heading text-xl text-forest-700">Toothpix</span>
            <span className="overline ml-2 hidden sm:inline">Live Demo</span>
          </Link>
          <Link to="/signup" data-testid="demo-signup" className="btn-primary !py-2 !px-5 text-xs">
            Start free trial <ArrowRight size={14} />
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 md:py-20">
        <div className="overline">Toothpix · Marketing demo</div>
        <h1 className="font-heading text-4xl md:text-5xl text-forest-700 mt-3 leading-tight">
          See what your patients will see.
        </h1>
        <p className="text-forest-400 mt-4 max-w-xl leading-relaxed">
          This is an actual Toothpix output on a real photo. Drag the handle
          to compare. On a real clinic page, the patient uploads their <em className="not-italic">own</em> selfie and sees themselves with straight teeth in about 30 seconds, then they land in your dashboard as a high-intent lead.
        </p>

        <div className="mt-10">
          {demo ? (
            <BeforeAfterSlider
              beforeSrc={imageUrl(demo.before_url)}
              afterSrc={imageUrl(demo.after_url)}
              aspect="aspect-square"
            />
          ) : (
            <div className="aspect-square w-full bg-bone-200/40 border border-bone-200 flex items-center justify-center">
              <div className="overline">Loading demo…</div>
            </div>
          )}
        </div>

        <div className="mt-12 bg-forest-600 text-white p-6 md:p-10">
          <div className="overline !text-bone-200/70">Want this for your clinic?</div>
          <h3 className="font-heading text-3xl mt-3">
            Start a free trial. 20 real smile previews, no card required.
          </h3>
          <p className="text-bone-100/80 mt-3 text-sm leading-relaxed">
            You get a branded <code className="font-mono text-bone-100">/preview/your-clinic</code> URL, a lead inbox, and analytics. Every patient who uses your link becomes a lead in your dashboard, not someone else's.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link to="/signup" data-testid="demo-cta-trial" className="inline-flex items-center gap-2 bg-white text-forest-600 px-6 py-3 text-sm font-medium hover:bg-bone-100">
              Start free trial <ArrowRight size={14} />
            </Link>
            <Link to="/" data-testid="demo-cta-home" className="inline-flex items-center gap-2 border border-white/40 text-white px-6 py-3 text-sm hover:bg-white/10">
              Back to homepage
            </Link>
          </div>
        </div>

        <p className="text-xs text-forest-400 mt-8 text-center">
          The patient-facing version is reserved for trial &amp; paid clinics so we never accidentally route your prospective patients to a competitor.
        </p>
      </main>
    </div>
  );
}
